import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  LinearProgress,
  Snackbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Alert,
  InputAdornment,
  Paper,
  Select,
  MenuItem
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { restaurantService } from '../../services/restaurantService';
import {
  Save,
  Refresh,
  Upload,
  Business,
  Phone,
  Email,
  Language,
  Palette,
  Image,
  Close,
  PhotoCamera,
  ContactMail,
  TableRestaurant,
  Restaurant,
  Visibility,
  Edit,
  Cancel,
  Payments,
  AccountBalanceWallet,
  CreditCard,
  AccountBalance,
  AttachMoney,
  Add,
  Calculate
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import SliderManagement from './SliderManagement';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const RestaurantConfig = () => {
  const [config, setConfig] = useState(null);
  const [emailConfig, setEmailConfig] = useState({
    catering_admin_emails: "",
    reservation_admin_emails: "",
    contact_admin_emails: ""
  })
  const [platformFees, setPlatformFees] = useState({
    paynow: {
      fixed_charges: { value: '', type: 'fixed' },
      extra_charges: { value: '', type: 'percentage' }
    },
    grabpay: {
      fixed_charges: { value: '', type: 'fixed' },
      extra_charges: { value: '', type: 'percentage' }
    },
    card: {
      fixed_charges: { value: '', type: 'fixed' },
      extra_charges: { value: '', type: 'percentage' }
    },
    upi: {
      fixed_charges: { value: '', type: 'fixed' },
      extra_charges: { value: '', type: 'percentage' }
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, cover: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [richText, setRichText] = useState("");
  const [validationError, setValidationError] = useState({
    field: '', message: ''
  });
  const [countryNoLength, setCountryNoLength] = useState(0)
  const [isEditing, setIsEditing] = useState(false);
  const [isPlatformEdit, setIsPlatformEdit] = useState(false)
  const [editValues, setEditValues] = useState({
    contact_admin_emails: '',
    reservation_admin_emails: '',
    catering_admin_emails: ''
  });
  const contactEmailsRef = useRef(null);
  const reservationEmailRef = useRef(null);
  const cateringEmailRef = useRef(null);

  const { showSuccess, showError } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));


  const paymentMethods = [
    {
      key: 'paynow',
      label: 'PayNow',
      icon: AccountBalanceWallet
    },
    {
      key: 'grabpay',
      label: 'GrabPay',
      icon: AccountBalanceWallet
    },
    {
      key: 'card',
      label: 'Credit/Debit Card',
      icon: CreditCard
    },
    {
      key: 'upi',
      label: 'UPI',
      icon: AccountBalance
    }
  ];

  const chargeTypes = [
    {
      key: 'fixed_charges',
      label: 'Fixed Charges',
      icon: AttachMoney,
      defaultType: 'fixed'
    },
    {
      key: 'extra_charges',
      label: 'Extra Charges',
      icon: Add,
      defaultType: 'percentage'
    }
  ];


  useEffect(() => {
    loadRestaurantConfig();
    // console.log("Data = ", config)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (config) {
      // Convert array to comma-separated string for editing
      setEmailConfig({
        contact_admin_emails: Array.isArray(config.contact_admin_emails)
          ? config.contact_admin_emails.join(', ')
          : config.contact_admin_emails || '',
        reservation_admin_emails: Array.isArray(config.reservation_admin_emails)
          ? config.reservation_admin_emails.join(', ')
          : config.reservation_admin_emails || '',
        catering_admin_emails: Array.isArray(config.catering_admin_emails)
          ? config.catering_admin_emails.join(', ')
          : config.catering_admin_emails || ''
      });

      const ensureFeeStructure = (feeObj) => ({
        fixed_charges: feeObj?.fixed_charges || { value: '', type: 'fixed' },
        extra_charges: feeObj?.extra_charges || { value: '', type: 'percentage' }
      });

      setPlatformFees({
        paynow: ensureFeeStructure(config.platform_fee?.pay_now),
        grabpay: ensureFeeStructure(config.platform_fee?.grab_pay),
        upi: ensureFeeStructure(config.platform_fee?.upi),
        card: ensureFeeStructure(config.platform_fee?.card)
      });
    }
  }, [config])

  const hasChanges = useMemo(() => {
    if (!config) return false;

    const currentContact = Array.isArray(config.contact_admin_emails)
      ? config.contact_admin_emails.join(', ')
      : config.contact_admin_emails || '';

    const currentReservation = Array.isArray(config.reservation_admin_emails)
      ? config.reservation_admin_emails.join(', ')
      : config.reservation_admin_emails || '';

    const currentCatering = Array.isArray(config.catering_admin_emails)
      ? config.catering_admin_emails.join(', ')
      : config.catering_admin_emails || '';

    return (
      emailConfig.contact_admin_emails !== currentContact ||
      emailConfig.reservation_admin_emails !== currentReservation ||
      emailConfig.catering_admin_emails !== currentCatering
    );
  }, [emailConfig, config]);

  const handleCancel = () => {
    // Reset emailConfig to current config values
    if (config) {
      setEmailConfig({
        contact_admin_emails: Array.isArray(config.contact_admin_emails)
          ? config.contact_admin_emails.join(', ')
          : config.contact_admin_emails || '',
        reservation_admin_emails: Array.isArray(config.reservation_admin_emails)
          ? config.reservation_admin_emails.join(', ')
          : config.reservation_admin_emails || '',
        catering_admin_emails: Array.isArray(config.catering_admin_emails)
          ? config.catering_admin_emails.join(', ')
          : config.catering_admin_emails || ''
      });
    }

    setIsEditing(false);
    setValidationError({ field: '', message: '' });
  };

  const handlePlatformCancel = () => {
    setIsPlatformEdit(false)
    const ensureFeeStructure = (feeObj) => ({
      fixed_charges: feeObj?.fixed_charges || { value: '', type: 'fixed' },
      extra_charges: feeObj?.extra_charges || { value: '', type: 'percentage' }
    });
    setPlatformFees({
      paynow: ensureFeeStructure(config.platform_fee?.pay_now),
      grabpay: ensureFeeStructure(config.platform_fee?.grab_pay),
      upi: ensureFeeStructure(config.platform_fee?.upi),
      card: ensureFeeStructure(config.platform_fee?.card)
    });

  }


  const hasInvalidEmails = (emailString) => {
    if (!emailString || typeof emailString !== 'string') return false;

    const emails = emailString
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emails.length === 0) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.some(email => !emailRegex.test(email));
  };


  const loadRestaurantConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await restaurantService.getRestaurantConfig();
      // console.log('Loaded restaurant config:', data);
      setConfig({
        ...data,
        theme_config: data?.theme_config || {
          primary_color: '#1976d2',
          secondary_color: '#dc004e',
          accent_color: '#ff9800',
        },
      });

    } catch (err) {
      setError('Failed to load restaurant configuration');
      showError('Failed to load restaurant configuration');
    } finally {
      setLoading(false);
    }
  };

  // Email validation function (update your existing stringToEmailArray)
  const stringToEmailArray = (emailString) => {
    // If it's not a string or is empty/whitespace only, return empty
    if (!emailString || typeof emailString !== 'string' || emailString.trim() === '') {
      return {
        valid: [],
        invalid: [],
        all: [],
        hasInvalid: false
      };
    }

    const emails = emailString
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Validate each email
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));

    return {
      valid: emails.filter(email => emailRegex.test(email)),
      invalid: invalidEmails,
      all: emails,
      hasInvalid: invalidEmails.length > 0
    };
  };


  const platformFeePercentageValidation = (method) => {
    // console.log("Method => ", method)
    if (method.fixed_charges["type"] === "percentage" && method.fixed_charges["value"] > 100) {
      return false
    }
    if (method.extra_charges["type"] === "percentage" && method.extra_charges["value"] > 100) {
      return false
    }
    return true
  }

  const handleSave = async () => {
    // console.log("Config = ", config)

    setValidationError({ field: '', message: '' })

    if (config.name.length === 0) {
      setValidationError({ field: 'name', message: "Please Enter Restaurant Name" })
      return;
    }

   

    if (!/^[A-Za-z\s]+$/.test(config.name)) {
      setValidationError({
        field: 'name',
        message: "Name should contain only letters"
      });
      return;
    }

    if (config.name.length > 25) {
      setValidationError({ field: 'name', message: "Enter Name Less than 25 Characters" })
      return;
    }
    if(!config.phone){
      setValidationError({ field: 'phone', message: 'Please Enter a Phone Number' })
      return;
    }
    if (config.phone.length !== countryNoLength && countryNoLength !== 0) {
      setValidationError({ field: 'phone', message: 'Please Enter a Valid Phone Number' })
      return;
    }

     if (!config.email) {
      setValidationError({ field: 'email', message: 'Please Enter a Email' })
      return;
    }

    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const email_verify = regex.test(config.email.trim());

   

    if (config.email !== "" && !email_verify) {
      setValidationError({ field: 'email', message: 'Please Enter a Valid Email' })
      return;
    }

    // Process email fields - allow empty strings to be saved as empty arrays
    const contactEmailResult = stringToEmailArray(emailConfig.contact_admin_emails.toLowerCase());
    const reservationEmailResult = stringToEmailArray(emailConfig.reservation_admin_emails.toLowerCase());
    const cateringEmailResult = stringToEmailArray(emailConfig.catering_admin_emails.toLowerCase());

    // IMPORTANT: Validate only if there are emails present
    if (contactEmailResult.all.length > 0 && contactEmailResult.hasInvalid) {
      setValidationError({
        field: 'contact_admin_emails',
        message: `Invalid email(s): ${contactEmailResult.invalid.join(', ')}`
      });
      return;
    }

    if (reservationEmailResult.all.length > 0 && reservationEmailResult.hasInvalid) {
      setValidationError({
        field: 'reservation_admin_emails',
        message: `Invalid email(s): ${reservationEmailResult.invalid.join(', ')}`
      });
      return;
    }

    if (cateringEmailResult.all.length > 0 && cateringEmailResult.hasInvalid) {
      setValidationError({
        field: 'catering_admin_emails',
        message: `Invalid email(s): ${cateringEmailResult.invalid.join(', ')}`
      });
      return;
    }
    // console.log("Platform Fees => ", platformFees)

    // console.log("Contact emails => ", contactEmailResult.valid);
    // console.log("Reservation emails => ", reservationEmailResult.valid);
    // console.log("Catering emails => ", cateringEmailResult.valid);




    if (!platformFeePercentageValidation(platformFees.paynow) || !platformFeePercentageValidation(platformFees.card) || !platformFeePercentageValidation(platformFees.upi) || !platformFeePercentageValidation(platformFees.grabpay)) {
      // console.log("Yes Error Occured")
      showError("Platform Fee Percentage is not greater than 100%")
      return;
    }

    config["pay_now"] = platformFees.paynow
    config["card"] = platformFees.card
    config["upi"] = platformFees.upi
    config["grab_pay"] = platformFees.grabpay

    // Always update with valid emails (empty array if no valid emails)
    // This ensures empty strings are saved as empty arrays
    config['contact_admin_emails'] = contactEmailResult.all.length > 0 ? contactEmailResult.valid : [];
    config['reservation_admin_emails'] = reservationEmailResult.all.length > 0 ? reservationEmailResult.valid : [];
    config['catering_admin_emails'] = cateringEmailResult.all.length > 0 ? cateringEmailResult.valid : [];

    // // console.log("website => ", config.)
    if (!config.website){
       setValidationError({
        field: 'website',
        message: 'Please enter a valid website starting with http, https, or www (if www, must end with .com)'
      });
    }
    if (config.website.trim() === "" ||
      (config.website.startsWith("www.") && !config.website.endsWith(".com")) ||
      (!config.website.startsWith("http://") && !config.website.startsWith("https://") && !config.website.startsWith("www."))
    ) {
      // // console.log("At Website Validation");
      setValidationError({
        field: 'website',
        message: 'Please enter a valid website starting with http, https, or www (if www, must end with .com)'
      });
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      setValidationError({ field: '', message: '' })


      // Log what we're sending
      // console.log('Sending email config to backend:', {
      //   contact_admin_emails: config.contact_admin_emails,
      //   reservation_admin_emails: config.reservation_admin_emails,
      //   catering_admin_emails: config.catering_admin_emails
      // });

      await restaurantService.updateRestaurantConfig(config);
      // setSuccess('Restaurant configuration updated successfully!');
      showSuccess('Restaurant configuration updated successfully!');
      setIsEditing(false)
      setIsPlatformEdit(false)

      // Reload config to get updated data
      await loadRestaurantConfig();
    } catch (err) {
      // setError('Failed to update restaurant configuration');
      showError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {

    setValidationError({ field: '', message: '' })

    if (field === "phone") {
      let phoneNumber_Length = value.target.countryValue.format.split('.').length - 1;
      setCountryNoLength(phoneNumber_Length)
      setConfig(prev => (prev ? {
        ...prev,
        [field]: value.target.value
      } : null));
    }
    else {
      setConfig(prev => (prev ? {
        ...prev,
        [field]: value
      } : null));
    }

  };

  const handleFeeChange = (method, chargeType, value) => {
    // console.log("Platform Value => ", value)
    setPlatformFees(prev => ({
      ...prev,
      [method]: {
        ...prev[method],
        [chargeType]: {
          ...prev[method][chargeType],
          value: value
        }
      }
    }));
  };

  const handleFeeTypeChange = (method, chargeType, type) => {
    setPlatformFees(prev => ({
      ...prev,
      [method]: {
        ...prev[method],
        [chargeType]: {
          ...prev[method][chargeType],
          type: type
        }
      }
    }));
  };

  const calculateExampleFee = (method, amount) => {
    const fees = platformFees[method];
    if (!fees) return '0.00';

    let totalFee = 0;

    // Calculate fixed charge
    if (fees.fixed_charges?.value) {
      if (fees.fixed_charges.type === 'percentage') {
        totalFee += (amount * parseFloat(fees.fixed_charges.value) / 100);
      } else {
        totalFee += parseFloat(fees.fixed_charges.value) || 0;
      }
    }

    // Calculate extra charge
    if (fees.extra_charges?.value) {
      if (fees.extra_charges.type === 'percentage') {
        totalFee += (amount * parseFloat(fees.extra_charges.value) / 100);
      } else {
        totalFee += parseFloat(fees.extra_charges.value) || 0;
      }
    }

    return totalFee.toFixed(2);
  };

  const handleThemeChange = (colorField, value) => {
    setConfig(prev => (prev ? {
      ...prev,
      theme_config: {
        ...(prev.theme_config || {}),
        [colorField]: value
      }
    } : null));
  };

  const validateImageDimensions = (file, maxWidth, maxHeight) => {
    return new Promise(resolve => {
      const img = new window.Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const { width, height } = img;
        URL.revokeObjectURL(img.src);

        if (width <= maxWidth && height <= maxHeight) {
          resolve(true);
        } else {
          resolve(false);
        }
      };

      img.onerror = () => resolve(false);
    });
  };



  const handleImageUpload = async (field, file) => {
    if (!file) return;

    const isValid = await validateImageDimensions(file, 600, 600);
    if (!isValid) {
      showError("Image must be 600px × 600px or less");
      return;
    }

    const imageType = field === 'logo_url' ? 'logo' : 'about_us_image_url' ? 'about' : 'cover';
    try {
      setUploading(prev => ({ ...prev, [imageType]: true }));
      setError('');
      const result = await restaurantService.uploadRestaurantImage(imageType, file);

      // console.log('Upload result:', result);

      // Update config with the full image URL returned by backend
      setConfig(prev => {
        const updated = {
          ...prev,
          [field]: result.image_url
        };
        // console.log('Updated config:', updated);
        return updated;
      });

      showSuccess(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} uploaded successfully!`);

    } catch (err) {
      const errorMsg = err.response?.data?.detail || `Failed to upload ${imageType}`;
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  if (loading || !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
        <CircularProgress />
      </Box>
    );
  }

  const handleEmailFieldChange = (key, value) => {
    // console.log(value);
    setValidationError({ field: '', message: '' });
    setEmailConfig(prev => ({ ...prev, [key]: value }))
  };


  const moveCursorToEnd = (inputRef) => {
    if (inputRef?.current) {
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  };

  // console.log(emailConfig);


  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2
      }}>
        <Box>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight="bold"
            gutterBottom
          >
            Restaurant Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your restaurant's branding and settings. Changes will reflect on the public website.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRestaurantConfig}
            disabled={loading}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Basic Information */}
        <Grid item xs={12} lg={8}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="primary" />
                Basic Information
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />

              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Restaurant Name"
                    value={config?.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    margin="none"
                    required
                    size={isMobile ? "small" : "medium"}
                  />
                  {validationError.field === 'name' ?
                    <Typography sx={{ textAlign: 'center', color: 'red' }}>{validationError.message}</Typography> : null
                  }

                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    country="sg"
                    specialLabel=""
                    value={config?.phone || ''}
                    onChange={(phone, country) => handleInputChange("phone", { target: { value: phone, countryValue: country } })}
                    inputStyle={{
                      width: '100%',
                      height: '50.25px',
                      borderRadius: '7px',
                      color: 'black',
                      paddingLeft: '58px',
                      fontSize: '12px !important',
                    }}
                    buttonStyle={{
                      borderTopLeftRadius: '7px',
                      borderBottomLeftRadius: '7px',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                  />
                  {/* <TextField
                    fullWidth
                    label="Phone Number"
                    value={config?.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    margin="none"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                  /> */}
                  {validationError.field === 'phone' ?
                    <Typography sx={{ textAlign: 'center', color: 'red' }}>{validationError.message}</Typography> : null
                  }
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={config?.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    margin="none"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                  {validationError.field === 'email' ?
                    <Typography sx={{ textAlign: 'center', color: 'red' }}>{validationError.message}</Typography> : null
                  }
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Website *"
                    value={config?.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    margin="none"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Language color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                  {validationError.field === 'website' ?
                    <Typography sx={{ textAlign: 'center', color: 'red' }}>{validationError.message}</Typography> : null
                  }
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={config?.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    margin="none"
                    multiline
                    rows={isMobile ? 4 : 3}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Logo and Images */}
        <Grid item xs={12} lg={4}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Image color="primary" />
                Branding
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />

              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Restaurant Logo (600 x 600)
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Avatar
                    src={config?.logo_url || undefined}
                    alt="Restaurant Logo"
                    sx={{
                      width: { xs: 80, sm: 80 },
                      height: { xs: 80, sm: 80 },
                      mx: { xs: 'auto', sm: 0 }
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={(e) => handleImageUpload('logo_url', e.target.files[0])}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                        disabled={uploading.logo}
                        fullWidth={isMobile}
                        size={isMobile ? "small" : "medium"}
                      >
                        {uploading.logo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                    {uploading.logo && (
                      <LinearProgress sx={{ mt: 1 }} />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Cover Image Upload */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Cover Image
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Avatar
                    src={config?.cover_image_url || undefined}
                    alt="Restaurant Cover"
                    sx={{
                      width: { xs: 120, sm: 120 },
                      height: { xs: 80, sm: 80 },
                      mx: { xs: 'auto', sm: 0 }
                    }}
                    variant="rounded"
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="cover-upload"
                      type="file"
                      onChange={(e) => handleImageUpload('cover_image_url', e.target.files[0])}
                    />
                    <label htmlFor="cover-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                        disabled={uploading.cover}
                        fullWidth={isMobile}
                        size={isMobile ? "small" : "medium"}
                      >
                        {uploading.cover ? 'Uploading...' : 'Upload Cover Image'}
                      </Button>
                    </label>
                    {uploading.cover && (
                      <LinearProgress sx={{ mt: 1 }} />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email color="primary" />
                  Admin Emails Configuration
                </Typography>

                {!isEditing ? (
                  <Button
                    startIcon={<Edit />}
                    variant="outlined"
                    onClick={() => setIsEditing(true)}
                    size="small"
                  >
                    Edit
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<Cancel />}
                      variant="outlined"
                      color="error"
                      onClick={handleCancel}
                      size="small"
                    >
                      Cancel
                    </Button>
                    {/* <Button 
              startIcon={<Save />} 
              variant="contained" 
              onClick={handleSave}  // Your existing handleSave function
              size="small"
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button> */}
                  </Box>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure Admin Email recipients for different types of inquiries
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />

              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    type="text"
                    label="Contact Form Emails"
                    placeholder="e.g., admin@example.com, support@example.com"
                    value={emailConfig?.contact_admin_emails || ''}
                    onChange={(e) => handleEmailFieldChange('contact_admin_emails', e.target.value)}
                    onFocus={() => moveCursorToEnd(contactEmailsRef)}
                    onClick={() => moveCursorToEnd(contactEmailsRef)}
                    margin="normal"
                    helperText="Multiple emails separated by commas"
                    disabled={!isEditing || saving}
                    error={validationError.field === 'contact_admin_emails'}
                    InputProps={{
                      sx: {
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                      },
                      // startAdornment: (
                      //   <InputAdornment position="start">
                      //     <ContactMail />
                      //   </InputAdornment>
                      // ),
                    }}
                  />
                  {validationError.field === 'contact_admin_emails' && (
                    <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                      {validationError.message}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    type="text"
                    label="Table Reservation Emails"
                    placeholder="e.g., reservations@example.com, manager@example.com"
                    value={emailConfig?.reservation_admin_emails || ''}
                    onChange={(e) => handleEmailFieldChange('reservation_admin_emails', e.target.value)}
                    onFocus={() => moveCursorToEnd(reservationEmailRef)}
                    onClick={() => moveCursorToEnd(reservationEmailRef)}
                    margin="normal"
                    helperText="Multiple emails separated by commas"
                    disabled={!isEditing || saving}
                    error={validationError.field === 'reservation_admin_emails'}
                    InputProps={{
                      sx: {
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                      },
                      // startAdornment: (
                      //   <InputAdornment position="start">
                      //     <TableRestaurant />
                      //   </InputAdornment>
                      // ),
                    }}
                  />
                  {validationError.field === 'reservation_admin_emails' && (
                    <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                      {validationError.message}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    type="text"
                    label="Catering Inquiry Emails"
                    placeholder="e.g., catering@example.com, events@example.com"
                    value={emailConfig?.catering_admin_emails || ''}
                    onChange={(e) => handleEmailFieldChange('catering_admin_emails', e.target.value)}
                    onFocus={() => moveCursorToEnd(cateringEmailRef)}
                    onClick={() => moveCursorToEnd(cateringEmailRef)}
                    margin="normal"
                    helperText="Multiple emails separated by commas"
                    disabled={!isEditing || saving}
                    error={validationError.field === 'catering_admin_emails'}
                    InputProps={{
                      sx: {
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                      },
                      // startAdornment: (
                      //   <InputAdornment position="start">
                      //     <Restaurant />
                      //   </InputAdornment>
                      // ),
                    }}
                  />
                  {validationError.field === 'catering_admin_emails' && (
                    <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                      {validationError.message}
                    </Alert>
                  )}
                </Grid>
              </Grid>

              {/* Email Preview */}
              <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Visibility />
                  Email Recipients Preview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <ContactMail color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500">Contact Form:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {config?.contact_admin_emails?.join(', ') || 'No emails configured'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <TableRestaurant color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500">Table Reservations:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {config?.reservation_admin_emails?.join(', ') || 'No emails configured'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Restaurant color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500">Catering Inquiries:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {config?.catering_admin_emails?.join(', ') || 'No emails configured'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payments color="primary" />
                  Platform Fee Configuration
                </Typography>

                {!isPlatformEdit ? (
                  <Button
                    startIcon={<Edit />}
                    variant="outlined"
                    onClick={() => setIsPlatformEdit(true)}
                    size="small"
                  >
                    Edit
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<Cancel />}
                      variant="outlined"
                      color="error"
                      onClick={handlePlatformCancel}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure platform fees for different payment methods. Fixed charges are applied per transaction, extra charges are percentage-based.
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />

              <Grid container spacing={isMobile ? 2 : 3}>
                {paymentMethods.map((method) => (
                  <Grid item xs={12} key={method.key}>
                    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <method.icon color="primary" />
                        <Typography variant="subtitle1" fontWeight="600">
                          {method.label}
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {chargeTypes.map((charge) => {
                          const feeData = platformFees?.[method.key]?.[charge.key] || {};

                          return (
                            <Grid item xs={12} sm={6} key={`${method.key}-${charge.key}`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {/* <charge.icon fontSize="small" /> */}
                                <Typography variant="body2" fontWeight="500">
                                  {charge.label}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>

                                {/* <TextField
                                  fullWidth
                                  type="text"
                                  placeholder="0%"
                                  value={feeData.value || ''}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;

                                    // Allow empty string
                                    if (inputValue === '') {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    // Allow numbers, decimal point, percentage sign
                                    const percentageRegex = /^-?\d*\.?\d*%?$/;

                                    if (!percentageRegex.test(inputValue)) {
                                      return; // Invalid input, don't update
                                    }

                                    // Check for multiple decimal points
                                    const decimalCount = (inputValue.match(/\./g) || []).length;
                                    if (decimalCount > 1) {
                                      return;
                                    }

                                    // Check for multiple percentage signs
                                    const percentCount = (inputValue.match(/%/g) || []).length;
                                    if (percentCount > 1) {
                                      return;
                                    }

                                    // Remove percentage sign for validation
                                    const numericValue = inputValue.replace('%', '');

                                    // Handle incomplete values
                                    if (numericValue === '' || numericValue === '-' || numericValue === '.') {
                                      handleFeeChange(method.key, charge.key, inputValue);
                                      return;
                                    }

                                    // LIMIT LEADING ZEROS: Prevent multiple leading zeros before decimal
                                    // Check for patterns like 00, 000, 001, 00.5, etc.
                                    if (numericValue.length > 1 && numericValue[0] === '0' && numericValue[1] !== '.') {
                                      // If we have leading zero followed by another digit (not decimal), 
                                      // remove the leading zero
                                      if (numericValue.includes('.')) {
                                        // For numbers like 00.5, keep only 0.5
                                        const parts = numericValue.split('.');
                                        const integerPart = parts[0];
                                        const decimalPart = parts[1] || '';

                                        // Remove all leading zeros from integer part
                                        const cleanInteger = integerPart.replace(/^0+/, '') || '0';
                                        const cleanValue = decimalPart ? `${cleanInteger}.${decimalPart}` : cleanInteger;

                                        handleFeeChange(method.key, charge.key, cleanValue + (inputValue.includes('%') ? '%' : ''));
                                        return;
                                      } else {
                                        // For whole numbers like 001, 0005, etc.
                                        // Remove leading zeros but keep at least one zero if all zeros
                                        const cleanValue = numericValue.replace(/^0+/, '') || '0';
                                        handleFeeChange(method.key, charge.key, cleanValue + (inputValue.includes('%') ? '%' : ''));
                                        return;
                                      }
                                    }

                                    // Check if it's a valid number
                                    const num = parseFloat(numericValue);
                                    if (isNaN(num)) {
                                      return;
                                    }

                                    // PERCENTAGE VALIDATION: Must be 0-100
                                    if (feeData.type === 'percentage') {
                                      if (num < 0 || num > 100) {
                                        return; // Invalid percentage
                                      }

                                      // Handle zeros - allow only one zero or 0.xx
                                      if (num === 0) {
                                        // Allow only: "0", "0.", "0.%", "0.0", "0.0%", "0.00", "0.00%"
                                        // Prevent: "00", "000", "000.0", etc.

                                        // Check if we have multiple zeros before decimal
                                        const match = numericValue.match(/^0+/);
                                        if (match && match[0].length > 1) {
                                          // More than one zero at start
                                          const cleanValue = '0' + numericValue.substring(match[0].length);
                                          handleFeeChange(method.key, charge.key, cleanValue + (inputValue.includes('%') ? '%' : ''));
                                          return;
                                        }

                                        // Allow 0, 0.0, 0.00 with max 2 decimals
                                        if (inputValue.includes('.')) {
                                          const decimalPart = numericValue.split('.')[1];
                                          if (decimalPart && decimalPart.length > 2) {
                                            return; // Too many decimal places
                                          }
                                        }
                                      }

                                      // Check decimal places (max 2)
                                      if (numericValue.includes('.')) {
                                        const decimalPart = numericValue.split('.')[1];
                                        if (decimalPart && decimalPart.length > 2) {
                                          return;
                                        }
                                      }
                                    }

                                    // Valid input, update state
                                    handleFeeChange(method.key, charge.key, inputValue);
                                  }}
                                  onBlur={(e) => {
                                    const inputValue = e.target.value;

                                    if (inputValue === '' || inputValue === '-' || inputValue === '.' || inputValue === '-.') {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    // Format on blur
                                    const numericValue = inputValue.replace('%', '');

                                    if (numericValue === '') {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    const num = parseFloat(numericValue);

                                    if (isNaN(num)) {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    // Clean up zeros on blur
                                    let cleanNumericValue = numericValue;

                                    // Remove leading zeros
                                    if (cleanNumericValue.length > 1 && cleanNumericValue[0] === '0' && cleanNumericValue[1] !== '.') {
                                      // Keep single zero
                                      cleanNumericValue = '0' + cleanNumericValue.substring(cleanNumericValue.replace(/^0+/, '').length ?
                                        cleanNumericValue.replace(/^0+/, '').length : 1);
                                    }

                                    // Remove trailing zeros after decimal point
                                    if (cleanNumericValue.includes('.')) {
                                      // Remove unnecessary trailing zeros
                                      cleanNumericValue = cleanNumericValue.replace(/(\.\d*?)0+$/, '$1');
                                      // Remove trailing decimal point if nothing after it
                                      cleanNumericValue = cleanNumericValue.replace(/\.$/, '');
                                    }

                                    // Final percentage validation
                                    if (feeData.type === 'percentage') {
                                      if (num < 0) {
                                        handleFeeChange(method.key, charge.key, '0%'); // Set to 0% if negative
                                      } else if (num > 100) {
                                        handleFeeChange(method.key, charge.key, '100%'); // Cap at 100%
                                      } else {
                                        // Format with percentage sign
                                        const formattedValue = inputValue.includes('%') ?
                                          cleanNumericValue + '%' :
                                          `${cleanNumericValue}%`;
                                        handleFeeChange(method.key, charge.key, formattedValue);
                                      }
                                    } else {
                                      // For non-percentage fields
                                      handleFeeChange(method.key, charge.key, cleanNumericValue);
                                    }
                                  }}
                                  disabled={!isPlatformEdit}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        {feeData.type === 'percentage' ? '%' : 'S$'}
                                      </InputAdornment>
                                    ),
                                  }}
                                  error={feeData.type === 'percentage' && feeData.value && feeData.value !== '' &&
                                    (() => {
                                      const numericValue = feeData.value.replace('%', '');
                                      const num = parseFloat(numericValue);
                                      return !isNaN(num) && (num < 0 || num > 100);
                                    })()
                                  }
                                  helperText={
                                    feeData.type === 'percentage' && feeData.value && feeData.value !== '' &&
                                    (() => {
                                      const numericValue = feeData.value.replace('%', '');
                                      const num = parseFloat(numericValue);
                                      if (!isNaN(num)) {
                                        if (num < 0) return "Percentage cannot be negative";
                                        if (num > 100) return "Percentage cannot exceed 100%";
                                      }
                                      return "";
                                    })()
                                  }
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1
                                    }
                                  }}
                                /> */}
                                <TextField
                                  fullWidth
                                  type="text"
                                  placeholder={feeData.type === 'percentage' ? "0%" : "0.00"}
                                  value={feeData.value || ''}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    const currentType = feeData.type || charge.defaultType;

                                    // Allow empty string
                                    if (inputValue === '') {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    // Different regex patterns based on fee type
                                    let regexPattern;
                                    if (currentType === 'percentage') {
                                      // Allow numbers, decimal point, percentage sign
                                      regexPattern = /^-?\d*\.?\d*$/;
                                    } else {
                                      // For fixed amount: allow numbers, decimal point, NO percentage sign
                                      regexPattern = /^-?\d*\.?\d*$/;
                                    }

                                    if (!regexPattern.test(inputValue)) {
                                      return; // Invalid input, don't update
                                    }

                                    // Check for multiple decimal points
                                    const decimalCount = (inputValue.match(/\./g) || []).length;
                                    if (decimalCount > 1) {
                                      return;
                                    }

                                    // For percentage type only: check for multiple percentage signs
                                    if (currentType === 'percentage') {
                                      const percentCount = (inputValue.match(/%/g) || []).length;
                                      if (percentCount > 1) {
                                        return;
                                      }
                                    } else {
                                      // For fixed type: REJECT any percentage sign
                                      if (inputValue.includes('%')) {
                                        return;
                                      }
                                    }

                                    // Remove percentage sign for validation (if present)
                                    const numericValue = currentType === 'percentage'
                                      ? inputValue.replace('%', '')
                                      : inputValue;

                                    // Handle incomplete values
                                    if (numericValue === '' || numericValue === '-' || numericValue === '.') {
                                      // For fixed type, don't allow % sign in incomplete values
                                      const valueToStore = currentType === 'percentage' ? inputValue : numericValue;
                                      handleFeeChange(method.key, charge.key, valueToStore);
                                      return;
                                    }

                                    // Handle leading zeros for both types
                                    if (numericValue.length > 1 && numericValue[0] === '0' && numericValue[1] !== '.') {
                                      if (numericValue.includes('.')) {
                                        // For numbers like 00.5, keep only 0.5
                                        const parts = numericValue.split('.');
                                        const integerPart = parts[0];
                                        const decimalPart = parts[1] || '';

                                        // Remove all leading zeros from integer part
                                        const cleanInteger = integerPart.replace(/^0+/, '') || '0';
                                        const cleanValue = decimalPart ? `${cleanInteger}.${decimalPart}` : cleanInteger;

                                        handleFeeChange(method.key, charge.key, cleanValue);
                                        return;
                                      } else {
                                        // For whole numbers like 001, 0005, etc.
                                        const cleanValue = numericValue.replace(/^0+/, '') || '0';
                                        handleFeeChange(method.key, charge.key, cleanValue);
                                        return;
                                      }
                                    }

                                    // Check if it's a valid number
                                    const num = parseFloat(numericValue);
                                    if (isNaN(num)) {
                                      return;
                                    }

                                    // PERCENTAGE VALIDATION: Must be 0-100
                                    if (currentType === 'percentage') {
                                      // Additional validation for percentage
                                      if (num < 0 || num > 100) {
                                        return; // Invalid percentage
                                      }

                                      // Check for specific percentage edge cases
                                      if (num === 0) {
                                        // Allow only: "0", "0.", "0.%", "0.0", "0.0%", "0.00", "0.00%"
                                        // Prevent: "00", "000", "000.0", etc.
                                        const match = numericValue.match(/^0+/);
                                        if (match && match[0].length > 1) {
                                          // More than one zero at start
                                          const cleanValue = '0' + numericValue.substring(match[0].length);
                                          handleFeeChange(method.key, charge.key, cleanValue);
                                          return;
                                        }
                                      }

                                      // Check decimal places (max 2 for percentage)
                                      if (numericValue.includes('.')) {
                                        const decimalPart = numericValue.split('.')[1];
                                        if (decimalPart && decimalPart.length > 2) {
                                          return;
                                        }
                                      }

                                      // Prevent values like 100.1, 100.01, etc.
                                      if (num > 100) {
                                        const integerPart = numericValue.split('.')[0];
                                        if (integerPart && parseInt(integerPart) > 100) {
                                          return;
                                        }
                                      }
                                    }
                                    // FIXED AMOUNT VALIDATION: Must be 0-1000
                                    else {
                                      if (num < 0 || num > 1000) {
                                        return; // Invalid fixed amount
                                      }

                                      // Check decimal places (max 2 for fixed amount)
                                      if (numericValue.includes('.')) {
                                        const decimalPart = numericValue.split('.')[1];
                                        if (decimalPart && decimalPart.length > 2) {
                                          return;
                                        }
                                      }
                                    }

                                    // Store value with % sign only for percentage type
                                    const valueToStore = currentType === 'percentage'
                                      ? (inputValue.includes('%') ? inputValue : numericValue)
                                      : numericValue;

                                    handleFeeChange(method.key, charge.key, valueToStore);
                                  }}
                                  onBlur={(e) => {
                                    const inputValue = e.target.value;
                                    const currentType = feeData.type || charge.defaultType;

                                    if (inputValue === '' || inputValue === '-' || inputValue === '.' || inputValue === '-.') {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    // Format on blur
                                    const numericValue = currentType === 'percentage'
                                      ? inputValue.replace('%', '')
                                      : inputValue;

                                    if (numericValue === '') {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    const num = parseFloat(numericValue);

                                    if (isNaN(num)) {
                                      handleFeeChange(method.key, charge.key, '');
                                      return;
                                    }

                                    // Clean up zeros on blur
                                    let cleanNumericValue = numericValue;

                                    // Remove leading zeros
                                    if (cleanNumericValue.length > 1 && cleanNumericValue[0] === '0' && cleanNumericValue[1] !== '.') {
                                      cleanNumericValue = '0' + cleanNumericValue.substring(cleanNumericValue.replace(/^0+/, '').length ?
                                        cleanNumericValue.replace(/^0+/, '').length : 1);
                                    }

                                    // Remove trailing zeros after decimal point
                                    if (cleanNumericValue.includes('.')) {
                                      // Remove unnecessary trailing zeros
                                      cleanNumericValue = cleanNumericValue.replace(/(\.\d*?)0+$/, '$1');
                                      // Remove trailing decimal point if nothing after it
                                      cleanNumericValue = cleanNumericValue.replace(/\.$/, '');
                                    }

                                    // Final validation based on type
                                    if (currentType === 'percentage') {
                                      let finalValue = num;

                                      // Clamp to 0-100 range
                                      if (num < 0) {
                                        finalValue = 0;
                                      } else if (num > 100) {
                                        finalValue = 100;
                                      }

                                      // For percentage, format with proper decimal places
                                      if (cleanNumericValue.includes('.')) {
                                        const decimalPart = cleanNumericValue.split('.')[1];
                                        // Limit to 2 decimal places
                                        if (decimalPart && decimalPart.length > 2) {
                                          cleanNumericValue = finalValue.toFixed(2);
                                        }
                                      }

                                      // Format with percentage sign (always add % sign on blur for percentage type)
                                      const formattedValue = `${cleanNumericValue}`;
                                      handleFeeChange(method.key, charge.key, formattedValue);
                                    } else {
                                      // Fixed amount validation
                                      let finalValue = num;

                                      // Clamp to 0-1000 range
                                      if (num < 0) {
                                        finalValue = 0;
                                      } else if (num > 1000) {
                                        finalValue = 1000;
                                      }

                                      // Format with 2 decimal places for fixed amounts
                                      let formattedValue;
                                      if (inputValue.includes('.')) {
                                        // Preserve user's decimal input if valid
                                        const decimalPart = numericValue.split('.')[1];
                                        if (decimalPart && decimalPart.length <= 2) {
                                          formattedValue = cleanNumericValue;
                                        } else {
                                          formattedValue = finalValue.toFixed(2);
                                        }
                                      } else if (inputValue.endsWith('.')) {
                                        // If user typed a dot at the end
                                        formattedValue = `${cleanNumericValue}.`;
                                      } else {
                                        // For whole numbers, add .00 for fixed amounts
                                        formattedValue = currentType === 'fixed' ? `${cleanNumericValue}.00` : cleanNumericValue;
                                      }

                                      handleFeeChange(method.key, charge.key, formattedValue);
                                    }
                                  }}
                                  disabled={!isPlatformEdit}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        {feeData.type === 'percentage' ? '%' : 'S$'}
                                      </InputAdornment>
                                    ),
                                  }}
                                  error={
                                    (feeData.value && feeData.value !== '') &&
                                    (() => {
                                      const currentType = feeData.type || charge.defaultType;
                                      const numericValue = currentType === 'percentage'
                                        ? feeData.value.replace('%', '')
                                        : feeData.value;
                                      const num = parseFloat(numericValue);

                                      if (!isNaN(num)) {
                                        if (currentType === 'percentage') {
                                          return num < 0 || num > 100;
                                        } else {
                                          return num < 0 || num > 1000;
                                        }
                                      }
                                      return false;
                                    })()
                                  }
                                  helperText={
                                    (feeData.value && feeData.value !== '') &&
                                    (() => {
                                      const currentType = feeData.type || charge.defaultType;
                                      const numericValue = currentType === 'percentage'
                                        ? feeData.value.replace('%', '')
                                        : feeData.value;
                                      const num = parseFloat(numericValue);

                                      if (!isNaN(num)) {
                                        if (currentType === 'percentage') {
                                          if (num < 0) return "Percentage cannot be negative";
                                          if (num > 100) return "Percentage cannot exceed 100%";
                                        } else {
                                          if (num < 0) return "Amount cannot be negative";
                                          if (num > 1000) return "Amount cannot exceed S$1000";
                                        }
                                      }
                                      return "";
                                    })()
                                  }
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1
                                    }
                                  }}
                                />


                                <Select
                                  value={feeData.type || charge.defaultType}
                                  onChange={(e) => handleFeeTypeChange(method.key, charge.key, e.target.value)}
                                  disabled={!isPlatformEdit}
                                  size="small"
                                  sx={{ minWidth: 120 }}
                                >
                                  <MenuItem value="fixed">Fixed (S$)</MenuItem>
                                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                                </Select>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Fee Calculation Preview */}
              {/* <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calculate />
                  Fee Calculation Example (Based on $100 transaction)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {['paynow', 'grabpay', 'card', 'upi'].map((method) => (
                    <Grid item xs={12} sm={6} md={3} key={method}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize', mb: 1 }}>
                          {method === 'grabpay' ? 'GrabPay' : method === 'paynow' ? 'PayNow' : method.toUpperCase()}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Fixed: {platformFees?.[method]?.fixed_charges?.value || '0'}
                            {platformFees?.[method]?.fixed_charges?.type === 'percentage' ? '%' : '$'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Extra: {platformFees?.[method]?.extra_charges?.value || '0'}
                            {platformFees?.[method]?.extra_charges?.type === 'percentage' ? '%' : '$'}
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography variant="body2" fontWeight="500">
                            Total Fee: ${calculateExampleFee(method, 100)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
        {/* Theme Configuration */}
        {/* <Grid item xs={12}> */}
        {/* <Card elevation={2}> */}
        {/* <CardContent sx={{ p: { xs: 2, sm: 3 } }}> */}
        {/* <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Palette color="primary" />
                Theme Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customize the color scheme for your public website
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} /> */}

        {/* <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Primary Color"
                    type="color"
                    value={config?.theme_config?.primary_color || '#000000'}
                    onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& input[type="color"]': { p: 1, height: 40 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Secondary Color"
                    type="color"
                    value={config?.theme_config?.secondary_color || '#000000'}
                    onChange={(e) => handleThemeChange('secondary_color', e.target.value)}
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& input[type="color"]': { p: 1, height: 40 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Accent Color"
                    type="color"
                    value={config?.theme_config?.accent_color || '#000000'}
                    onChange={(e) => handleThemeChange('accent_color', e.target.value)}
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& input[type="color"]': { p: 1, height: 40 } }}
                  />
                </Grid>
              </Grid> */}

        {/* Color Preview */}
        {/* <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Color Preview
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: config?.theme_config?.primary_color,
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography variant="body2">Primary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: config?.theme_config?.secondary_color,
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography variant="body2">Secondary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: config?.theme_config?.accent_color,
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography variant="body2">Accent</Typography>
                  </Box>
                </Box>
              </Box> */}
        {/* </CardContent> */}
        {/* </Card> */}
        {/* </Grid> */}

        {/* Slider Images Management */}
        {/* <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Image color="primary" />
                Hero Slider Images
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage slider images for the homepage hero section. Upload multiple images to create an engaging carousel.
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />

              <SliderManagement />
            </CardContent>
          </Card>
        </Grid> */}

        {/* <Grid item xs={12}>

          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                About Section
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manange the About Section
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />
              <Typography
                sx={{ fontWeight: 600, mb: 2 }}>Upload About Section Image</Typography>

              <Box sx={{
                display: 'flex',
                gap: 5,
                alignItems: 'center',
                mb: 2
              }}>
                <Avatar
                  src={config?.about_us_image_url || undefined}
                  alt="Restaurant About Image"
                  sx={{
                    width: { xs: 120, sm: 120, md: 180 },
                    height: { xs: 80, sm: 80, md: 180 },
                    mx: { xs: 'auto', sm: 0 }
                  }}
                  variant="rounded"
                />

                <Box sx={{ flexGrow: 1 }}>
                  <input
                    accept="image/*, video/mp4, video/*"
                    style={{ display: 'none' }}
                    id="about-upload"
                    type="file"
                    onChange={(e) => handleImageUpload('about_us_image_url', e.target.files[0])}
                  />
                  <label htmlFor="about-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      disabled={uploading.logo}
                      fullWidth={isMobile}
                      size={isMobile ? "small" : "medium"}
                    >
                      {uploading.logo ? 'Uploading...' : config?.about_us_image_url ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>
                  {uploading.logo && (
                    <LinearProgress sx={{ mt: 1 }} />
                  )}
                </Box>
              </Box>

              <Typography sx={{ fontWeight: 600, mb: 2 }}>About Us Description</Typography>

              <ReactQuill
                sx={{ width: '50%' }}
                theme="snow"
                value={config?.about_us_description || ''}
                onChange={(content) => handleInputChange('about_us_description', content)}
                placeholder="Write description..."
              />

            </CardContent>
          </Card>
        </Grid> */}

      </Grid>
    </Box>
  );
};

export default RestaurantConfig;
